import { syntaxTree } from '@codemirror/language';
import type { EditorState } from '@codemirror/state';
import type { SyntaxNodeRef, Tree, TreeCursor } from '@lezer/common';

import { getTextFromRange } from './get-text.js';

/** @internal */
export function getStatementCursorAtPosition(
  cursorPosition: number,
  editorState: EditorState,
) {
  const tree = syntaxTree(editorState);
  let cursor = tree.cursorAt(cursorPosition, -1);

  if (cursor.node.name === 'Filter' || cursor.node.name === 'LogicalOperator') {
    cursor = tree.cursorAt(cursorPosition, 1);
  }

  while (
    cursor.node.name !== 'FilterStatement' &&
    cursor.node.name !== 'LogicalOperator' &&
    cursor.parent()
  ) {
    // Intentionally left empty.
  }

  return cursor;
}

/**
 * Check whether the statement at the cursor position has a next statement.
 * Also returns true, if the current statement is only followed by a space.
 * @internal
 */
export function hasNextStatement(
  cursorPosition: number,
  editorState: EditorState,
): boolean {
  const cursor = getStatementCursorAtPosition(cursorPosition, editorState);

  let hasNext =
    cursor.nextSibling() &&
    (cursor.node.name === 'FilterStatement' ||
      cursor.node.name === 'LogicalOperator' ||
      cursor.node.name === 'FilterGroup') &&
    cursor.from > cursorPosition;

  if (hasNext) {
    return true;
  }

  const { to } = cursor.node;
  if (cursor.parent()) {
    hasNext = to === cursor.node.to - 1;
  }

  return hasNext;
}

/**
 * Check whether the node is inside a Bracket/Group.
 * @internal
 */
export function isCursorInsideGroup(
  editorState: EditorState,
  cursorPosition: number,
) {
  const tree = syntaxTree(editorState);
  const cursor: TreeCursor = tree.cursorAt(cursorPosition, -1);

  while (cursor) {
    if (['OpeningBracket', 'FilterGroup'].includes(cursor.node?.name)) {
      return true;
    }

    if (!cursor.parent()) {
      break;
    }
  }
  return false;
}

/**
 * Check whether the node at the cursor position has a next sibling.
 * @internal
 */
export function hasNextSibling(
  cursorPosition: number,
  editorState: EditorState,
  options: {
    ignorePositionOperators?: boolean;
  } = {},
): boolean {
  const { ignorePositionOperators } = options;
  const tree = syntaxTree(editorState);
  let cursor = tree.cursorAt(cursorPosition, 1);

  if (hasNextStatement(cursorPosition, editorState)) {
    return true;
  }

  if (cursor.node.name === 'Filter' || cursor.node.name === 'FilterStatement') {
    cursor = tree.cursorAt(cursorPosition, -1);
  }

  // If the cursor is positioned before an opening bracket, move to the parent to get to the Group / List node.
  // The next sibling check should not consider children of Group / List nodes.
  if (cursor.node.name === 'OpeningBracket') {
    cursor.parent();
  }

  // If the cursor is right before a closing bracket and the used operator is a list operator, we definitely have a next sibling.
  if (
    cursor.node.name === 'ClosingBracket' &&
    cursorPosition === cursor.node.from
  ) {
    const bracketCursor = tree.cursorAt(cursor.node.from, -1);
    // Move backwards until we find OpeningBracket
    while (
      bracketCursor.prev() &&
      bracketCursor.node.name !== 'OpeningBracket'
    ) {
      // Keep moving backwards until we find OpeningBracket
    }

    bracketCursor.prev();
    return (
      bracketCursor.node.name === 'FilterIncludesValue' ||
      bracketCursor.node.name === 'InclusionOperator'
    );
  }

  let { to } = cursor.node;

  if (
    ignorePositionOperators &&
    (cursor.node.name === 'PositionOperator' ||
      cursor.node.name === 'InvalidPositionOperator')
  ) {
    cursor.nextSibling();
    ({ to } = cursor.node);
  }

  if (
    cursor.node.name === 'Identifier' ||
    cursor.node.name === 'DoubleQuotes' ||
    cursor.node.name === 'StringContent'
  ) {
    cursor.parent();
    ({ to } = cursor.node);
  }

  // to correctly check the sibling we need to move up one more if we
  // we were previously in DoubleQuotes and are now in a String node
  if (cursor.node.name === 'String') {
    cursor.parent();
    ({ to } = cursor.node);
  }

  let nextSibling = cursor.nextSibling();
  // Ignore all consecutive error nodes. They are combined into one node in the tree conversion.
  while (nextSibling && cursor.node.name === '⚠') {
    nextSibling = cursor.nextSibling();
    ({ to } = cursor.node);
  }

  const hasNext = nextSibling && to < cursor.node.to;

  return hasNext;
}

/** @internal */
export function hasTrailingSpace(doc?: string, node?: SyntaxNodeRef): boolean {
  if (doc === undefined) {
    return false;
  }

  let text = doc;
  if (node) {
    text = getTextFromRange(node, doc.toString());
  }

  return text.slice(-1) === ' ';
}

/** @internal */
export function getPositionOperators(node: SyntaxNodeRef, tree: Tree) {
  let stringTreeCursor = tree.cursorAt(node.to, 1);
  const startsWithOperator = stringTreeCursor.node.name === 'PositionOperator';

  stringTreeCursor = tree.cursorAt(node.from, -1);
  const endsWithOperator = stringTreeCursor.node.name === 'PositionOperator';

  return { startsWithOperator, endsWithOperator };
}

/** @internal */
export function getPositionOperatorsInString(value: string): {
  hasEndsWithOperator: boolean;
  hasStartsWithOperator: boolean;
} {
  // For e.g. 'a = "\*b", the value of the node would only be '*b', so for
  // checking whether a string has a position operator inside the doublequotes,
  // we need to use the textValue.
  // The regex checks whether there's an asterisk inside the doublequotes.
  // See https://regex101.com/r/QS92XR/1
  const matches = value.matchAll(/(?:^\*?(?:"\*))|(?:(?:(?<!\\)\*")(?:\*?$))/g);

  let hasEndsWithOperator = false;
  let hasStartsWithOperator = false;
  for (const match of matches) {
    if (match.index === 0) {
      hasEndsWithOperator = true;
    }
    if (match.index > 0) {
      hasStartsWithOperator = true;
    }
  }

  return {
    hasEndsWithOperator,
    hasStartsWithOperator,
  };
}
